// Jotai atoms and hooks for authentication.

import { atom, useAtom, useSetAtom } from 'jotai';
import { atomWithMutation } from 'jotai-tanstack-query';
import { authService } from '@/services';
import { clearQueryCache } from '@/lib/query-client';
import { onUserLogout } from '@/lib/sentry/sentry-user-context';
import { PULSE_DATE_RANGE_KEY } from '@/stores';
import { clearPermissionsAtom, clearAllPermissionsStorage } from '@/stores/permissions.store';
import { selectedBrandAtom } from '@/stores/sentinel-workspace.store';
import { clearAccountScopedStorage } from '@/lib/account-storage';
import { clearAiEditSessions } from '@/modules/protocols/utils/ai-edit-session-store';

// Storage keys
const ACCESS_TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const CURRENT_USER_KEY = 'currentUser';
// Holds the admin's session while they impersonate another user
const ADMIN_SNAPSHOT_KEY = 'adminSnapshot';

// User type
export interface AuthUser {
	id: string;
	email: string;
	name?: string;
	accountId: number;
	accountName?: string;
	subDomain?: string;
	/** Agent sign-off appended on send. Backend does not return it yet. */
	messageSignature?: string | null;
	customAttributes?: Record<string, unknown>;
}

// Auth state atoms - writable atom that triggers re-renders
const authStateAtom = atom(!!localStorage.getItem(ACCESS_TOKEN_KEY));

export const isAuthenticatedAtom = atom(
	(get) => get(authStateAtom),
	(_get, set, update: boolean) => {
		set(authStateAtom, update);
	}
);

// Access token atom - reads from localStorage. Read again whenever the session changes: with
// nothing to depend on it kept its first answer, so signing in after the first render (an SSO
// link) left it empty and the socket never authenticated until a reload.
export const accessTokenAtom = atom((get) => {
	get(authStateAtom);
	get(currentUserAtom);
	return localStorage.getItem(ACCESS_TOKEN_KEY);
});

// Helper function to get initial user from localStorage
const getInitialUser = (): AuthUser | null => {
	const userJson = localStorage.getItem(CURRENT_USER_KEY);
	if (!userJson) return null;

	try {
		return JSON.parse(userJson) as AuthUser;
	} catch {
		return null;
	}
};

// Current user atom - writable atom that can be updated
export const currentUserAtom = atom<AuthUser | null>(getInitialUser());

// Saved admin session captured before impersonation
export interface AdminSnapshot {
	accessToken: string;
	refreshToken: string;
	user: AuthUser;
}

const getInitialSnapshot = (): AdminSnapshot | null => {
	const json = localStorage.getItem(ADMIN_SNAPSHOT_KEY);
	if (!json) return null;

	try {
		return JSON.parse(json) as AdminSnapshot;
	} catch {
		return null;
	}
};

// Admin snapshot atom - non-null only while impersonating
export const adminSnapshotAtom = atom<AdminSnapshot | null>(getInitialSnapshot());

// Login mutation atom
const loginMutationAtom = atomWithMutation(() => ({
	mutationKey: ['auth', 'login'],
	mutationFn: async ({ email, password }: { email: string; password: string }) => {
		return authService.login(email, password);
	},
	onSuccess: (data) => {
		// Store tokens
		localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
		localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
		// Store user data
		localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(data.user));
		console.log('[Auth] Login successful, user stored:', data.user);
	}
}));

// Forgot-password mutation atom (no auth state change)
const forgotPasswordMutationAtom = atomWithMutation(() => ({
	mutationKey: ['auth', 'forgot-password'],
	mutationFn: async ({ email }: { email: string }) => {
		return authService.forgotPassword(email);
	}
}));

// Set-password mutation atom — persists tokens like login (user is signed in after)
const setPasswordMutationAtom = atomWithMutation(() => ({
	mutationKey: ['auth', 'set-password'],
	mutationFn: async ({ token, password }: { token: string; password: string }) => {
		return authService.setPassword(token, password);
	},
	onSuccess: (data) => {
		localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
		localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
		localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(data.user));
		console.log('[Auth] Set-password successful, user stored:', data.user);
	}
}));

// Logout mutation atom
const logoutMutationAtom = atomWithMutation(() => ({
	mutationKey: ['auth', 'logout'],
	mutationFn: async () => {
		// Clear all auth data
		localStorage.removeItem(ACCESS_TOKEN_KEY);
		localStorage.removeItem(REFRESH_TOKEN_KEY);
		localStorage.removeItem(CURRENT_USER_KEY);
		localStorage.removeItem(ADMIN_SNAPSHOT_KEY);
		localStorage.removeItem(PULSE_DATE_RANGE_KEY);
		// Clear cached permissions for every account
		clearAllPermissionsStorage();
		clearAccountScopedStorage();
		// Unfinished AI edit sessions hold protocol drafts; they don't outlive the login.
		void clearAiEditSessions();
		// Clear all queries
		clearQueryCache();
		// Clear Sentry user context
		onUserLogout();
	}
}));

// Impersonate mutation atom
const impersonateMutationAtom = atomWithMutation(() => ({
	mutationKey: ['auth', 'impersonate'],
	mutationFn: async ({ userId, accountId }: { userId: string; accountId?: string }) => {
		return authService.impersonate(userId, accountId);
	},
	onSuccess: (data) => {
		// Store tokens
		localStorage.setItem(ACCESS_TOKEN_KEY, data.accessToken);
		localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
		// Store user data
		localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(data.user));
		// Impersonation swaps the account under the same tab, so the previous
		// account's scenarios and simulate-tools switch must not carry over.
		clearAccountScopedStorage();
		clearQueryCache();
		console.log('[Auth] Impersonation successful, user stored:', data.user);
	}
}));

/** Returns the best display name for the current user. */
function getDisplayName(user: AuthUser | null | undefined): string {
	return user?.name || user?.email?.split('@')[0] || 'User';
}

/** Returns two-character initials for the current user. */
function getUserInitials(user: AuthUser | null | undefined): string {
	const source = user?.name || user?.email?.split('@')[0] || '';
	if (!source) return 'U';
	const words = source.trim().split(/\s+/);
	if (words.length >= 2 && words[0] && words[1]) {
		return (words[0][0] + words[1][0]).toUpperCase();
	}
	return source.slice(0, 2).toUpperCase();
}

// Hooks
export function useAuth() {
	const [isAuthenticated, setIsAuthenticated] = useAtom(isAuthenticatedAtom);
	const [currentUser] = useAtom(currentUserAtom);
	const [accessToken] = useAtom(accessTokenAtom);

	return {
		isAuthenticated,
		currentUser,
		accessToken,
		setIsAuthenticated,
		displayName: getDisplayName(currentUser),
		userInitials: getUserInitials(currentUser)
	};
}

export function useLogin() {
	const [loginMutation] = useAtom(loginMutationAtom);
	const [, setIsAuthenticated] = useAtom(isAuthenticatedAtom);
	const [, setCurrentUser] = useAtom(currentUserAtom);

	return {
		...loginMutation,
		mutateAsync: async (variables: { email: string; password: string }) => {
			const result = await loginMutation.mutateAsync(variables);
			// Update auth state after successful login
			setIsAuthenticated(true);
			// Update current user atom to trigger re-renders
			// localStorage is already updated in onSuccess callback
			setCurrentUser(result.user);
			return result;
		}
	};
}

export function useForgotPassword() {
	const [forgotPasswordMutation] = useAtom(forgotPasswordMutationAtom);
	return forgotPasswordMutation;
}

export function useSetPassword() {
	const [setPasswordMutation] = useAtom(setPasswordMutationAtom);
	const [, setIsAuthenticated] = useAtom(isAuthenticatedAtom);
	const [, setCurrentUser] = useAtom(currentUserAtom);

	return {
		...setPasswordMutation,
		mutateAsync: async (variables: { token: string; password: string }) => {
			const result = await setPasswordMutation.mutateAsync(variables);
			// User is signed in after setting a password — mirror useLogin.
			setIsAuthenticated(true);
			setCurrentUser(result.user);
			return result;
		}
	};
}

export function useLogout() {
	const [logoutMutation] = useAtom(logoutMutationAtom);
	const [, setIsAuthenticated] = useAtom(isAuthenticatedAtom);
	const [, setCurrentUser] = useAtom(currentUserAtom);
	const clearPerms = useSetAtom(clearPermissionsAtom);
	const [, setAdminSnapshot] = useAtom(adminSnapshotAtom);
	const setSelectedBrand = useSetAtom(selectedBrandAtom);

	return {
		...logoutMutation,
		mutateAsync: async () => {
			await logoutMutation.mutateAsync();
			// Update auth state after successful logout
			setIsAuthenticated(false);
			// Clear current user atom
			// localStorage is already cleared in mutation
			setCurrentUser(null);
			// Drop any saved admin snapshot (localStorage already cleared in mutation)
			setAdminSnapshot(null);
			// Drop the permissions atom so a re-login fetches fresh
			clearPerms();
			// The brand gate is per login — the next one picks again
			setSelectedBrand(null);
			// Sentry user context is already cleared in mutation
		}
	};
}

export function useImpersonate() {
	const [impersonateMutation] = useAtom(impersonateMutationAtom);
	const [, setIsAuthenticated] = useAtom(isAuthenticatedAtom);
	const [, setCurrentUser] = useAtom(currentUserAtom);
	const [, setAdminSnapshot] = useAtom(adminSnapshotAtom);

	return {
		...impersonateMutation,
		mutateAsync: async (variables: { userId: string; accountId?: string }) => {
			// Capture the current (admin) session BEFORE the mutation overwrites localStorage.
			// Read into locals first so a failed mutation never persists a snapshot.
			const existingSnapshot = localStorage.getItem(ADMIN_SNAPSHOT_KEY);
			const adminAccess = localStorage.getItem(ACCESS_TOKEN_KEY);
			const adminRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
			const adminUserJson = localStorage.getItem(CURRENT_USER_KEY);

			const result = await impersonateMutation.mutateAsync(variables);

			// Persist the admin snapshot only once (preserve the original admin across
			// nested impersonations) and only after a successful impersonation.
			if (!existingSnapshot && adminAccess && adminRefresh && adminUserJson) {
				const snapshot: AdminSnapshot = {
					accessToken: adminAccess,
					refreshToken: adminRefresh,
					user: JSON.parse(adminUserJson) as AuthUser
				};
				localStorage.setItem(ADMIN_SNAPSHOT_KEY, JSON.stringify(snapshot));
				setAdminSnapshot(snapshot);
			}

			// Update auth state after successful impersonation
			setIsAuthenticated(true);
			// Update current user atom to trigger re-renders
			// localStorage is already updated in onSuccess callback
			setCurrentUser(result.user);
			// Impersonation swaps the identity under the same tab. Every always-mounted query
			// atom (account flags, permissions, …) keeps its observer, so clearQueryCache()
			// alone leaves the previous account's data on screen — the intent-filtering
			// toggle showed the admin account's OFF while the customer was ON. Reload, the
			// same way useSwitchAccount does.
			window.location.assign('/');
			return result;
		}
	};
}

/** Restores the admin session saved before impersonation. */
export function useRestoreAdmin() {
	const [adminSnapshot] = useAtom(adminSnapshotAtom);

	return () => {
		if (!adminSnapshot) return false;

		// Restore admin tokens/user and drop the snapshot
		localStorage.setItem(ACCESS_TOKEN_KEY, adminSnapshot.accessToken);
		localStorage.setItem(REFRESH_TOKEN_KEY, adminSnapshot.refreshToken);
		localStorage.setItem(CURRENT_USER_KEY, JSON.stringify(adminSnapshot.user));
		localStorage.removeItem(ADMIN_SNAPSHOT_KEY);
		// Coming BACK from impersonation swaps the account just as going in does, so the
		// impersonated account's working data must not follow the admin out. The selected
		// BUSINESS UNIT is the load-bearing one: a brand belongs to one account, so leaving
		// it set scopes every admin screen by an id the admin's account does not own, which
		// reads as empty lists rather than an error. Going in already does this in
		// impersonateMutationAtom's onSuccess; this is the other half.
		clearAccountScopedStorage();
		return true;
	};
}

/** Returns whether the user is a super admin. */
export function isSuperAdmin(user: AuthUser | null | undefined): boolean {
	if (!user) return false;
	return user.customAttributes?.is_superadmin === true;
}
